/**
 * Trading System - pure market buy/sell logic + portfolio math
 * All functions are side-effect free (unit-testable).
 */
import type { ExchangeHolding, ExchangeState } from '../exchangeState.js';

/** Tolerance for floating point holding comparisons */
const EPSILON = 1e-9;

export interface BuyResult {
  coins: number;
  holding: ExchangeHolding;
  boughtAmount: number;
}

export interface SellResult {
  coins: number;
  holding: ExchangeHolding | null;
  proceeds: number;
  realizedProfit: number;
}

/**
 * Market buy: spend game coins to acquire `spend / price` units.
 * Returns null when the order is invalid (insufficient funds, bad input).
 */
export function executeBuy(
  coins: number,
  holding: ExchangeHolding | undefined,
  price: number,
  spend: number
): BuyResult | null {
  if (!(spend > 0) || !(price > 0)) return null;
  if (coins + EPSILON < spend) return null;

  const boughtAmount = spend / price;
  const prevAmount = holding?.amount ?? 0;
  const prevInvested = prevAmount * (holding?.avgPrice ?? 0);
  const newAmount = prevAmount + boughtAmount;
  const avgPrice = (prevInvested + spend) / newAmount;

  return {
    coins: Math.max(0, coins - spend),
    holding: { amount: newAmount, avgPrice },
    boughtAmount,
  };
}

/**
 * Market sell: sell `amount` units at `price` for game coins.
 * Returns null when the order is invalid (no holding, oversell, bad input).
 */
export function executeSell(
  coins: number,
  holding: ExchangeHolding | undefined,
  price: number,
  amount: number
): SellResult | null {
  if (!holding || !(amount > 0) || !(price > 0)) return null;
  if (holding.amount + EPSILON < amount) return null;

  const sellAmount = Math.min(amount, holding.amount);
  const proceeds = sellAmount * price;
  const realizedProfit = (price - holding.avgPrice) * sellAmount;
  const remaining = holding.amount - sellAmount;
  const emptied = remaining <= EPSILON;

  return {
    coins: coins + proceeds,
    holding: emptied ? null : { amount: remaining, avgPrice: holding.avgPrice },
    proceeds,
    realizedProfit,
  };
}

/** Unrealized profit/loss in game coins */
export function getUnrealizedPnl(holding: ExchangeHolding, price: number): number {
  return (price - holding.avgPrice) * holding.amount;
}

/** Profit/loss percent vs average entry price */
export function getPnlPercent(holding: ExchangeHolding, price: number): number {
  if (holding.avgPrice <= 0) return 0;
  return ((price - holding.avgPrice) / holding.avgPrice) * 100;
}

/** Current market value of all holdings */
export function getHoldingsValue(exchange: ExchangeState): number {
  let total = 0;
  for (const [coinId, holding] of Object.entries(exchange.holdings)) {
    const coin = exchange.coins[coinId];
    if (coin) total += holding.amount * coin.price;
  }
  return total;
}

/** Total invested (cost basis) across all holdings */
export function getTotalInvested(exchange: ExchangeState): number {
  let total = 0;
  for (const holding of Object.values(exchange.holdings)) {
    total += holding.amount * holding.avgPrice;
  }
  return total;
}

/** Portfolio-wide unrealized PnL percent (0 when nothing is held) */
export function getPortfolioPnlPercent(exchange: ExchangeState): number {
  const invested = getTotalInvested(exchange);
  if (invested <= 0) return 0;
  return ((getHoldingsValue(exchange) - invested) / invested) * 100;
}
